import { styled } from '@mui/material/styles';
import { PriorityType } from '../../enums';
import { Box, Typography, Button, IconButton, AppBar as MuiAppBar, Drawer as MuiDrawer } from '@mui/material';

interface AppBarProps extends React.ComponentProps<typeof MuiAppBar> {
  open?: boolean;
  isSidebarOpen?: boolean;
}

const drawerWidth = 240;

export const ProjectContainer = styled(Box)({
  display: 'flex',
  height: '100vh',
  backgroundColor: '#0d1828',
});

export const MainContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSidebarOpen',
})<{ isSidebarOpen?: boolean }>(({ theme, isSidebarOpen }) => ({
  flexGrow: 1,
  padding: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  ...(isSidebarOpen && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflow: 'hidden',
}));

export const AppBar = styled(MuiAppBar)<AppBarProps>(({ theme, isSidebarOpen }) => ({
  backgroundColor: '#162238',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  zIndex: theme.zIndex.drawer + 1,
  height: '64px',
  display: 'flex',
  flexDirection: 'row',
  ...(isSidebarOpen && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export const AppBarContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: '100%',
  padding: '0 16px',
});

export const ProjectTitle = styled(Typography)({
  fontWeight: 'bold',
  fontSize: '18px',
  color: 'white',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const SearchIcon = styled(IconButton)({
  marginLeft: '16px',
  color: 'rgba(255, 255, 255, 0.7)',
});

export const HeaderActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const HeaderButton = styled(Button)({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: '8px',
  padding: '6px 16px',
});

export const HeaderIconButton = styled(IconButton)({
  color: 'rgba(255, 255, 255, 0.7)',
  '&:hover': {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
});

export const MenuDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'width',
})<{ open?: boolean; width?: number }>(({ theme, open, width = drawerWidth }) => ({
  width: open ? width : theme.spacing(7),
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    backgroundColor: '#1E2E46',
    color: 'white',
    width: open ? width : theme.spacing(7),
    overflowX: 'hidden',
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

export const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 2),
  minHeight: 64,
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

export const ProjectSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ open = true }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  opacity: open ? 1 : 0,
  visibility: open ? 'visible' : 'hidden',
  height: open ? 'auto' : 0,
  overflow: 'hidden'
}));

export const ProjectName = styled(Typography)({
  fontSize: '16px',
  fontWeight: 'bold',
  color: 'white',
  marginBottom: '4px',
});

export const ProjectDescription = styled(Typography)({
  fontSize: '14px',
  color: 'rgba(255, 255, 255, 0.7)',
  marginTop: '8px',
  padding: '4px 0',
  lineHeight: '1.4',
  maxHeight: '80px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
  },
});

export const SectionTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ open = true }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.7)',
  padding: '16px 16px 8px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  opacity: open ? 1 : 0,
  visibility: open ? 'visible' : 'hidden',
  height: open ? 'auto' : 0,
  overflow: 'hidden',
  marginBottom: '4px', // Adicionando margem inferior para espaçamento
  display: 'block', // Garante que o título ocupe toda a largura
}));

export const BoardList = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ open = true }) => ({
  display: 'flex',
  flexDirection: 'column',
  opacity: open ? 1 : 0,
  visibility: open ? 'visible' : 'hidden',
  maxHeight: open ? 'calc(100vh - 400px)' : 0, // Altura dinâmica baseada na viewport
  overflowY: 'auto', // Adiciona scroll vertical quando necessário
  overflowX: 'hidden', // Esconde o overflow horizontal
  margin: '0 8px', // Pequena margem para o scroll não ficar colado nas bordas
  // Estilo personalizado para a barra de rolagem
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.3)',
    },
  },
}));

export const BoardItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 10px 8px 16px', // Ajustado padding para deixar espaço para o scrollbar
  borderRadius: '4px',
  margin: '2px 4px', // Reduzido margin lateral para acomodar o scrollbar
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: active ? 'rgba(33, 150, 243, 0.15)' : 'transparent',
  color: active ? '#2196f3' : 'white',
  '&:hover': {
    backgroundColor: active ? 'rgba(33, 150, 243, 0.2)' : 'rgba(255, 255, 255, 0.05)',
  },
}));

export const BoardItemIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color?: string }>(({ color }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '2px',
  backgroundColor: color || '#2196f3',
  marginRight: '12px',
  flexShrink: 0,
}));

export const BoardItemText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const AddBoardButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ open = true }) => ({
  margin: '8px 12px 8px 12px', // Ajustado margin para ficar consistente com a lista
  textTransform: 'none',
  color: 'rgba(255, 255, 255, 0.7)',
  justifyContent: 'flex-start',
  opacity: open ? 1 : 0,
  visibility: open ? 'visible' : 'hidden',
  height: open ? 'auto' : 0,
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
  },
}));

export const ContentArea = styled(Box)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: '#0d1828',
});

export const KanbanBoardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{ backgroundColor?: string }>(({ backgroundColor }) => ({
  backgroundColor: backgroundColor || '#0D2249',
  flexGrow: 1,
  overflowY: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
}));

export const BoardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 16px 16px',
});

export const BoardTitleArea = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const BoardTitleText = styled(Typography)({
  fontSize: '20px',
  fontWeight: 'bold',
  color: 'white',
});

export const BoardDescription = styled(Typography)({
  fontSize: '14px',
  color: 'rgba(255, 255, 255, 0.7)',
});

export const BoardControls = styled(Box)({
  display: 'flex',
  gap: '8px',
});

export const ColumnsContainer = styled(Box)({
  display: 'flex',
  overflowX: 'auto',
  overflowY: 'hidden',
  padding: '8px 16px 16px',
  flexGrow: 1,
  gap: '16px',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '8px',
  },
});

export const Column = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: '280px',
  maxWidth: '280px',
  backgroundColor: 'rgba(0, 0, 0, 0.16)',
  borderRadius: '8px',
  padding: '8px',
  height: '100%',
  transition: 'box-shadow 0.2s ease, opacity 0.2s ease',
  '&:hover': {
    boxShadow: '0 0 10px rgba(255, 255, 255, 0.1)',
  },
  '&.column-drag-over': {
    boxShadow: 'inset 0 -3px 0 rgba(33, 150, 243, 0.8)',
  }
}));

export const ColumnHeader = styled(Box)({  
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
padding: '8px',
marginBottom: '8px',
borderRadius: '4px',
cursor: 'grab',
'&:hover': {
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  '&:active': {
    cursor: 'grabbing',
  }
});

export const ColumnTitle = styled(Typography)({
  fontWeight: 'bold',
  color: 'white',
  fontSize: '14px',
});

export const ColumnCount = styled(Typography)({
  color: 'rgba(255, 255, 255, 0.5)',
  fontSize: '12px',
  padding: '2px 8px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '10px',
});

export const ColumnContent = styled(Box)({
  overflowY: 'auto',
  flexGrow: 1,
  padding: '4px',
  maxHeight: 'calc(100vh - 220px)', // Altura máxima para permitir scroll
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
  },
});

export const Card = styled(Box)({  
backgroundColor: '#1E2E46',
borderRadius: '8px',
padding: '12px',
marginBottom: '8px',
boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
cursor: 'grab',
transition: 'transform 0.15s, box-shadow 0.15s',
'&:hover': {
transform: 'translateY(-2px)',
boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
},
'&:active': {
cursor: 'grabbing',
},
'&.drag-over-top': {
borderTop: '2px solid rgba(33, 150, 243, 0.8)'
},
  '&.drag-over-bottom': {
    borderBottom: '2px solid rgba(33, 150, 243, 0.8)'
  }
});

export const CardTitle = styled(Typography)({  
fontSize: '14px',
fontWeight: 500,
color: 'white',
marginBottom: '8px',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    color: '#2196f3',
  },
});

export const CardContent = styled(Box)({  
marginBottom: '8px',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
  },
});

export const CardDescription = styled(Typography)({
  fontSize: '12px',
  color: 'rgba(255, 255, 255, 0.7)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

export const CardLabels = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  marginBottom: '8px',
});

export const CardLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  height: '8px',
  width: '32px',
  borderRadius: '4px',
  backgroundColor: color,
}));

export const CardFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '8px',
});

export const CardMeta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const CardMetaItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: '12px',
  '& svg': {
    fontSize: '14px',
    marginRight: '2px',
  },
});

export const CardDueDate = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isOverdue',
})<{ isOverdue?: boolean }>(({ isOverdue }) => ({
  fontSize: '12px',
  color: isOverdue ? '#f44336' : 'rgba(255, 255, 255, 0.6)',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

export const CardAssignee = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const AssigneeAvatar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'src',
})<{ src?: string }>(({ src }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: src ? 'transparent' : 'rgba(33, 150, 243, 0.5)',
  backgroundImage: src ? `url(${src})` : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  fontSize: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
}));

export const PriorityBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'priority',
})<{ priority?: PriorityType }>(({ priority }) => {
  const getColor = () => {
    switch (priority) {
      case 'high':
        return '#f44336';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return 'rgba(255, 255, 255, 0.3)';
    }
  };

  return {
    backgroundColor: getColor(),
    color: 'white',
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  };
});

export const AddButton = styled(Button)({
  color: 'rgba(255, 255, 255, 0.7)',
  justifyContent: 'flex-start',
  textTransform: 'none',
  padding: '8px',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
  },
});

export const AddColumnButton = styled(Button)({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
  justifyContent: 'flex-start',
  textTransform: 'none',
  padding: '16px',
  minWidth: '280px',
  maxWidth: '280px',
  height: 'fit-content',
  borderRadius: '8px',
  marginRight: '16px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
});

export const EmptyBoard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px',
  textAlign: 'center',
  height: '100%',
});

export const EmptyBoardIcon = styled(Box)({
  fontSize: '48px',
  color: 'rgba(255, 255, 255, 0.2)',
  marginBottom: '16px',
});

export const EmptyBoardTitle = styled(Typography)({
  fontSize: '24px',
  fontWeight: 'bold',
  color: 'white',
  marginBottom: '8px',
});

export const EmptyBoardText = styled(Typography)({
  fontSize: '16px',
  color: 'rgba(255, 255, 255, 0.6)',
  maxWidth: '400px',
  marginBottom: '24px',
});
